import React, { useCallback, useMemo, useState } from 'react';

import { ExclamationOutlined } from '@ant-design/icons';
import { Input, Modal, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { useCreateBatchAssociatePartners } from '@/features/lp-village/partners/api/createBatchAssociatePartners';
import {
  useSearchAvailablePartners,
  IAvailablePartner,
} from '@/features/lp-village/partners/api/getSearchAvailablePartners';

const { Text } = Typography;

interface IAssociateOrganizationModalProps {
  parentPartnerId: string;
  open: boolean;
  onClose: () => void;
}

const COLUMNS: ColumnsType<IAvailablePartner> = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    render: (id: string) => <Text copyable>{id}</Text>,
  },
];

export const AssociateOrganizationModal = ({
  parentPartnerId,
  open,
  onClose,
}: IAssociateOrganizationModalProps) => {
  const [modal, contextHolder] = Modal.useModal();
  const { mutateAsync, isPending } = useCreateBatchAssociatePartners();

  // searchTerm drives the React Query hook — set on Search click
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const {
    data: results = [],
    isFetching,
    isFetched,
  } = useSearchAvailablePartners(searchTerm);

  const handleSearch = useCallback((value: string) => {
    if (!value.trim()) return;

    setSelectedIds([]);
    setSearchTerm(value.trim());
  }, []);

  const selectedNames = useMemo(
    () => results.filter((r) => selectedIds.includes(r.id)).map((r) => r.name),
    [results, selectedIds]
  );

  const handleSubmit = useCallback(() => {
    if (selectedIds.length === 0) return;

    modal.confirm({
      title: 'Confirm Link',
      icon: <ExclamationOutlined />,
      content: `You are about to link ${selectedIds.length} partner(s) to this company : ${selectedNames.join(', ')}. This action will make them child organizations.`,
      okText: 'Link',

      onOk: async () => {
        try {
          await mutateAsync({
            parentPartnerId,
            partnerIds: selectedIds,
          });

          setSelectedIds([]);
          setSearchTerm('');
          onClose();
        } catch {
          // Error toast handled by useCreateBatchAssociatePartners onError
        }
      },
    });
  }, [
    selectedIds,
    selectedNames,
    mutateAsync,
    parentPartnerId,
    onClose,
    modal,
  ]);

  const handleClose = useCallback(() => {
    setSelectedIds([]);
    setSearchTerm('');
    onClose();
  }, [onClose]);

  return (
    <Modal
      data-testid="organization-associate-modal"
      title="Link Organizations"
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      okText={`Link (${selectedIds.length})`}
      okButtonProps={{
        disabled: selectedIds.length === 0,
        loading: isPending,
        'data-testid': 'organization-associate-confirm-button',
      }}
      width={{ xs: '100%', sm: 600 }}
    >
      {contextHolder}
      <Input.Search
        data-testid="organization-associate-search"
        placeholder="Search for an organization by name..."
        onSearch={handleSearch}
        enterButton="Search"
        loading={isFetching}
        allowClear
      />

      {isFetched && (
        <Table<IAvailablePartner>
          data-testid="organization-associate-results"
          columns={COLUMNS}
          dataSource={results}
          rowKey="id"
          pagination={false}
          scroll={{ x: true }}
          loading={isFetching}
          size="small"
          locale={{ emptyText: 'No organizations found' }}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys as string[]),
          }}
        />
      )}
    </Modal>
  );
};
